import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ConfigureDeviceModal = ({ isOpen, onClose, device, onUpdate }) => {
  const [formData, setFormData] = useState({
    deviceName: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    isActive: true,
    syncedMetrics: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (device) {
      setFormData({
        deviceName: device?.deviceName || '',
        apiKey: device?.apiKey || '',
        apiSecret: device?.apiSecret || '',
        accessToken: device?.accessToken || '',
        isActive: device?.isActive ?? true,
        syncedMetrics: device?.syncedMetrics || []
      });
    }
  }, [device]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    const { data, error: updateError } = await onUpdate(device?.id, formData);

    setLoading(false);

    if (updateError) {
      setError(updateError?.message || 'Failed to update device');
    } else {
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const metricOptions = [
    { value: 'heart_rate', label: 'Heart Rate' },
    { value: 'steps', label: 'Steps' },
    { value: 'calories', label: 'Calories' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'workouts', label: 'Workouts' },
    { value: 'distance', label: 'Distance' }
  ];

  const toggleMetric = (metric) => {
    const current = formData?.syncedMetrics || [];
    const updated = current?.includes(metric)
      ? current?.filter(m => m !== metric)
      : [...current, metric];
    handleChange('syncedMetrics', updated);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-card rounded-xl p-6 w-full max-w-2xl elevation-4 max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Settings" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Configure Device</h2>
                  <p className="text-sm text-muted-foreground">Update device settings and credentials</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Device Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Device Name *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., My Garmin Watch"
                  value={formData?.deviceName}
                  onChange={(e) => handleChange('deviceName', e?.target?.value)}
                  required
                />
              </div>

              {/* Device Status */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.isActive}
                    onChange={(e) => handleChange('isActive', e?.target?.checked)}
                    className="w-5 h-5 rounded border-wellness-light text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Active Device</span>
                    <p className="text-xs text-muted-foreground">Enable automatic data synchronization</p>
                  </div>
                </label>
              </div>

              {/* API Credentials */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">API Credentials</h3>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    API Key *
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your API key"
                    value={formData?.apiKey}
                    onChange={(e) => handleChange('apiKey', e?.target?.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    API Secret
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter your API secret (if required)"
                    value={formData?.apiSecret}
                    onChange={(e) => handleChange('apiSecret', e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Access Token
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter access token (if required)"
                    value={formData?.accessToken}
                    onChange={(e) => handleChange('accessToken', e?.target?.value)}
                  />
                </div>
              </div>

              {/* Metrics to Sync */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Metrics to Sync
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {metricOptions?.map((metric) => (
                    <button
                      key={metric?.value}
                      type="button"
                      onClick={() => toggleMetric(metric?.value)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        formData?.syncedMetrics?.includes(metric?.value)
                          ? 'border-primary bg-primary/10 text-primary' :'border-wellness-light text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      {metric?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={20} className="text-error" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading || !formData?.deviceName || !formData?.apiKey}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfigureDeviceModal;
